import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { DateTime, Duration } from 'luxon';
import { useLocation, useNavigate } from 'react-router-dom';
import { getLang } from 'shared/store/selectors/lang.selector';
import { getUser } from 'shared/store/selectors/user.selector';
import { isAccountAdmin, isCollaborator, isRoot } from 'shared/store/selectors/auth.selector';
import { findBestMatchingSchedule } from 'shared/utils/rotations';
import { sortByAlphabet } from 'shared/utils';
import { INTERNAL_ROUTING } from 'shared/constants';
import {
  useAllRotations,
  useTimeslots,
  useRotation,
  useTrainees,
} from 'shared/hooks/rotations.hooks';
import _ from 'lodash';

const RotationsContext = createContext();

export function useRotationsContext() {
  return useContext(RotationsContext);
}

const ROTATION_SCHEDULE_RANGE = Duration.fromObject({ month: 12 });

/**
 * Rotations provider — UI state (tab, filter, selected rotation) plus a
 * read-only view over rotation server state. Server state is owned by the
 * react-query cache; mutations live in `shared/hooks/rotations.hooks` and are
 * called directly by consumer components.
 *
 * `analyticsTriggers` is exposed on the context so consumers can fire the
 * page-level analytics events after their own mutation calls resolve.
 *
 * @export
 * @param {{
 *  children: React.ReactNode;
 *  analyticsTriggers: {
 *    onSidebarTabChange: (payload) => void;
 *    onSearch: (payload) => void;
 *    onRotationDetailsUpdated: (payload) => void;
 *    onResourceAdded: (payload) => void;
 *    onResourceDeleted: (payload) => void;
 *    onAddTeamMember: (payload) => void;
 *    onRemoveTeamMember: (payload) => void;
 *  }
 * }} props
 */
export function RotationsProvider({ children, analyticsTriggers }) {
  const navigate = useNavigate();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);

  const setQueryParams = (key, value) => searchParams.set(key, value);
  const getQueryParams = key => searchParams.get(key);

  const lang = useSelector(getLang('ROTATIONS'));
  const user = useSelector(getUser);
  const isAdmin = useSelector(isAccountAdmin);
  const isCollab = useSelector(isCollaborator);
  const isRootUser = useSelector(isRoot);

  const [filter, setFilter] = useState(
    getQueryParams(INTERNAL_ROUTING.QUERY_PARAMS.ROTATIONS_FILTER) || '',
  );
  const [acgmeOnly, setAcgmeOnly] = useState(false);
  const [tab, setTab] = useState(getQueryParams(INTERNAL_ROUTING.QUERY_PARAMS.ROTATIONS_TAB) || '');
  const [rotationId, setRotationId] = useState(
    getQueryParams(INTERNAL_ROUTING.QUERY_PARAMS.ROTATIONS) || null,
  );

  // ---------- Server reads ----------
  const allRotationsQuery = useAllRotations({ enabled: !!user.id });

  const timeslotsRange = useMemo(() => {
    // Bucket to start-of-day so the cache key is stable across remounts
    // within the same day; otherwise millisecond drift would miss the cache
    // on every /rotations route change.
    const today = DateTime.now().startOf('day');
    return {
      start: today.toJSDate(),
      end: today.plus(ROTATION_SCHEDULE_RANGE).toJSDate(),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);
  // `retry: 1` — this read gates the whole page (see `loading`), and the
  // default backoff would hold the spinner ~7s on a failing slot read.
  const timeslotsQuery = useTimeslots(
    { userIds: [user.id], ...timeslotsRange },
    { enabled: !!user.id, retry: 1 },
  );

  const rotationQuery = useRotation(rotationId);
  const traineesQuery = useTrainees(rotationId);

  const allRotations = useMemo(() => allRotationsQuery.data ?? [], [allRotationsQuery.data]);
  const mySchedule = useMemo(
    () =>
      (timeslotsQuery.data ?? []).map(({ start, end, rotation }) => ({
        ...rotation,
        start,
        end,
      })),
    [timeslotsQuery.data],
  );
  const myRotations = useMemo(
    () =>
      allRotations.filter(rotation => rotation.ownerIds?.includes(user.id) || rotation?.isStaff),
    [allRotations, user.id],
  );

  // selectedRotation is the detail query data with trainees folded in to
  // match the legacy `team.trainees` shape consumers expect.
  const selectedRotation = useMemo(() => {
    if (!rotationId || !rotationQuery.data) return null;
    return {
      ...rotationQuery.data,
      team: {
        ...(rotationQuery.data.team || {}),
        trainees: traineesQuery.data ?? [],
      },
    };
  }, [rotationId, rotationQuery.data, traineesQuery.data]);

  const isOwner = isCollab && selectedRotation?.ownerIds?.includes(user.id);
  const canEdit = isAdmin || isOwner;
  const canEditSettings = isRootUser;

  // ---------- Derived schedule grouping ----------
  const { schedules, bestMatchingSchedule } = useMemo(() => {
    const rotationSchedulePairs = allRotations.map(rotation => ({
      scheduleId: rotation.schedule.id,
      scheduleName: rotation.schedule.name,
      configuration: rotation.schedule.configuration || {},
      rotation,
    }));

    const rotationsBySchedule = _.map(
      _.groupBy(rotationSchedulePairs, 'scheduleId'),
      (pairs, scheduleId) => ({
        scheduleId,
        name: pairs[0].scheduleName,
        configuration: pairs[0].configuration,
        rotations: _.map(pairs, 'rotation'),
      }),
    );

    return {
      schedules: rotationsBySchedule.sort((a, b) => sortByAlphabet(a.name, b.name)),
      bestMatchingSchedule: findBestMatchingSchedule(rotationsBySchedule, user),
    };
  }, [allRotations, user]);

  // ---------- URL sync ----------
  useEffect(() => {
    if (!rotationId?.length) {
      searchParams.delete(INTERNAL_ROUTING.QUERY_PARAMS.ROTATIONS);
    } else {
      searchParams.set(INTERNAL_ROUTING.QUERY_PARAMS.ROTATIONS, rotationId);
    }
    navigate({ search: searchParams.toString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rotationId]);

  useEffect(() => {
    if (!filter?.length) {
      searchParams.delete(INTERNAL_ROUTING.QUERY_PARAMS.ROTATIONS_FILTER);
    } else {
      searchParams.set(INTERNAL_ROUTING.QUERY_PARAMS.ROTATIONS_FILTER, filter);
    }
    navigate({ search: searchParams.toString() });
    analyticsTriggers?.onSearch({ filter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    searchParams.set(INTERNAL_ROUTING.QUERY_PARAMS.ROTATIONS_TAB, tab);
    navigate({ search: searchParams.toString() });
    analyticsTriggers?.onSidebarTabChange({ tab });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Both reads gate the defaults below (and the first render, via `loading`):
  // deciding on partial data raced the two queries, and the rotations list
  // usually wins, which stranded slot-holders on a schedule tab.
  const settled = !allRotationsQuery.isPending && !timeslotsQuery.isPending;

  // The sidebar tabs that actually render — the one list both the Tabs
  // component and the defaults below read, so they can't drift apart.
  const visibleTabKeys = useMemo(
    () => [
      ...(mySchedule.length ? [lang.MY_SCHEDULE] : []),
      ...schedules.map(({ scheduleId }) => scheduleId),
      ...(myRotations.length ? [lang.MY_ROTATIONS] : []),
    ],
    [mySchedule.length, schedules, myRotations.length, lang.MY_SCHEDULE, lang.MY_ROTATIONS],
  );

  // MY_SCHEDULE whenever the user has slots, otherwise the best-matching (or
  // first) schedule. A `tab` restored from the URL that no longer renders
  // (slots ended, schedule emptied) falls back the same way.
  const defaultTab = mySchedule.length
    ? lang.MY_SCHEDULE
    : ((bestMatchingSchedule ?? schedules[0])?.scheduleId ?? '');
  const activeTab = tab && visibleTabKeys.includes(tab) ? tab : defaultTab;

  useEffect(() => {
    if (!settled || tab === activeTab || !activeTab) return;
    setTab(activeTab);
  }, [settled, tab, activeTab]);

  // URL → selection: a deep link, or a row click (which navigates). Reacts to
  // the URL only — re-reading it on list refetches raced the navigation that
  // writes a freshly-set id (after a delete-with-migration the stale URL
  // pulled the selection back to the deleted rotation).
  const routeId = searchParams.get(INTERNAL_ROUTING.QUERY_PARAMS.ROTATIONS);
  useEffect(() => {
    if (routeId) setRotationId(routeId);
  }, [routeId]);

  // Nothing selected — or a selection that no longer exists — once settled:
  // pick the active tab's first rotation. Skipped while the list refetches
  // (a delete invalidates it) so the pick never comes from the stale list.
  useEffect(() => {
    if (routeId || !settled || allRotationsQuery.isFetching) return;

    const known = new Set([...allRotations, ...mySchedule].map(({ id }) => id));
    if (rotationId && known.has(rotationId)) return;

    const rotationsOfTab =
      activeTab === lang.MY_SCHEDULE
        ? mySchedule
        : activeTab === lang.MY_ROTATIONS
          ? myRotations
          : (schedules.find(({ scheduleId }) => scheduleId === activeTab)?.rotations ?? []);
    const id = rotationsOfTab[0]?.id || allRotations[0]?.id || null;
    if (id !== rotationId) setRotationId(id);
  }, [
    routeId,
    settled,
    allRotationsQuery.isFetching,
    rotationId,
    activeTab,
    allRotations,
    mySchedule,
    myRotations,
    schedules,
    lang.MY_SCHEDULE,
    lang.MY_ROTATIONS,
  ]);

  const value = {
    lang,
    analyticsTriggers,
    myRotations,
    mySchedule,
    allRotations,
    schedules,
    visibleTabKeys,
    tab,
    filter,
    acgmeOnly,
    loading: !settled && !!user.id,
    error: allRotationsQuery.error?.message ?? timeslotsQuery.error?.message ?? '',
    setTab,
    setFilter,
    setAcgmeOnly,
    selectedRotation,
    loadingRotation: !!rotationId && rotationQuery.isPending,
    errorRotation: rotationQuery.error?.message ?? '',
    setQueryParams,
    getQueryParams,
    setRotationId,
    canEdit,
    canEditSettings,
  };

  return <RotationsContext.Provider value={value}>{children}</RotationsContext.Provider>;
}
